<template>
    <div class="col-lg-8 col-md-12 resa-row mx-auto sizer">

        <div class="picker">

            <!-- ── Header ── -->
            <div class="picker__header">
                <span class="picker__title">Choisissez votre date</span>
            </div>

            <!-- ── Body ── -->
            <div class="picker__body">

                <!-- Loader overlay -->
                <div v-if="isLoading" class="picker__loader">
                    <div class="spinner"></div>
                </div>

                <!-- ════ ÉTAPE 1 — Calendrier ════ -->
                <template v-if="!isLoading">

                    <!-- Navigation mois -->
                    <div class="cal-nav">
                        <button class="cal-nav__btn" @click="prevMonth" :disabled="isCurrentMonth">&#8249;</button>
                        <span class="cal-nav__month">{{ monthLabel }}</span>
                        <button class="cal-nav__btn" @click="nextMonth">&#8250;</button>
                    </div>

                    <!-- Grille calendrier -->
                    <div class="cal-grid">
                        <div v-for="d in dayNames" :key="d" class="cal-day-name">{{ d }}</div>

                        <!-- Cellules vides pour l'offset du 1er jour -->
                        <div v-for="n in firstDayOffset" :key="'e' + n" class="cal-day cal-day--empty"></div>

                        <!-- Jours du mois -->
                        <div
                            v-for="day in daysInMonth"
                            :key="day"
                            class="cal-day"
                            :class="{
                                'cal-day--today':    isToday(day),
                                'cal-day--disabled': isPast(day) || !hasSlot(day),
                                'cal-day--has-slot': hasSlot(day),
                                'cal-day--selected': isSelected(day),
                            }"
                            @click="selectDay(day)"
                        >
                            {{ day }}
                        </div>
                    </div>

                    <div class="legend">
                        <span><span class="legend-dot"></span>Créneaux disponibles</span>
                    </div>

                    <div class="divider"></div>

                    <!-- <template v-if="canBookMultiple">
                        <p class="step-title">Participants</p>
                        <div class="quantity-row">
                            <button class="qty-btn" type="button" @click="setArticleQuantity(article_quantity - 1)" :disabled="article_quantity <= 1">-</button>
                            <span class="qty-value">{{ participantQuantityLabel }}</span>
                            <button class="qty-btn" type="button" @click="setArticleQuantity(article_quantity + 1)" :disabled="article_quantity >= maxBookableArticleQuantity">+</button>
                        </div>
                        <div class="divider"></div>
                    </template> -->

                    <!-- ════ ÉTAPE 2 — Praticien(ne) ════ -->
                    <p class="step-title">Praticien(ne)</p>

                    <div v-if="staffList.length" class="staff-grid">
                        <div
                            v-for="staff in staffList"
                            :key="staff.id"
                            class="staff-card"
                            :class="{ active: currentStaff && currentStaff.id === staff.id }"
                            @click="selectStaff(staff)"
                        >
                            <div class="staff-avatar">
                                <img v-if="staff.image" :src="staff.image" :alt="staff.first_name" />
                                <span v-else>{{ initials(staff) }}</span>
                            </div>
                            {{ staff.id === 0 ? 'Pas de préférence' : staff.first_name }}
                        </div>
                    </div>
                    <p v-else class="empty-text">Sélectionnez une date pour voir les praticiens disponibles.</p>

                    <div class="divider"></div>

                    <!-- ════ ÉTAPE 3 — Créneaux ════ -->
                    <p class="step-title">Créneaux disponibles</p>

                    <div v-if="timeSlots.length" class="slots-row">
                        <div
                            v-for="slot in filteredTimeSlots"
                            :key="slot.time"
                            class="slot"
                            :class="{ active: currentSlot && currentSlot.time === slot.time }"
                            @click="selectSlot(slot)"
                        >
                            {{ slot.time }}
                        </div>
                    </div>

                    <div v-else-if="selectedDate && !isLoading" class="empty">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2">
                            <rect x="3" y="4" width="18" height="18" rx="2"/>
                            <line x1="3" y1="9" x2="21" y2="9"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="9" y1="14" x2="15" y2="14"/>
                        </svg>
                        <p>Aucun créneau disponible ce jour.</p>
                    </div>

                    <div v-else-if="!selectedDate" class="empty">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2">
                            <rect x="3" y="4" width="18" height="18" rx="2"/>
                            <line x1="3" y1="9" x2="21" y2="9"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                        </svg>
                        <p>Sélectionnez une date pour voir les créneaux.</p>
                    </div>

                </template>

            </div>

            <!-- ── Footer ── -->
            <div class="picker__footer">
                <div class="summary">
                    <template v-if="selectedDate && currentSlot">
                        <strong>{{ summaryDate }}</strong> à <strong>{{ currentSlot.time }}</strong><br>
                        avec <strong>{{ currentStaff && currentStaff.id !== 0 ? currentStaff.first_name : 'Pas de préférence' }}</strong>
                    </template>
                    <span v-else class="summary--empty">Sélectionnez une date et un créneau</span>
                </div>

                <button
                    class="btn-confirm"
                    :disabled="!currentSlot"
                    :class="{ 'btn-confirm--disabled': !currentSlot }"
                    @click.prevent.stop="confirm"
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Confirmer
                </button>
            </div>

        </div>

        <!-- Hidden inputs pour le form -->
        <input type="hidden" id="prestation" name="prestation" :value="JSON.stringify(prestation)" />
        <input type="hidden" id="select"      name="select"     :value="JSON.stringify(selectedSlot)" />
        <input type="hidden" id="practi"      name="practi"     :value="JSON.stringify(selectPracti)" />
        <input type="hidden" id="space"       name="space"      :value="JSON.stringify(selectSpace)" />
        <input type="hidden" id="type"        name="type"       :value="type" />
        <input type="hidden" id="nbUser"      name="nbUser"     :value="nb_user" />
        <input type="hidden" id="ok"          name="ok"         :value="1" />
        <input type="hidden" id="all"         name="all"        :value="JSON.stringify(allPrestations)" />

        <input v-if="type === 'serie'"      type="hidden" name="serie_user_id"     :value="serie_user_id" />
        <input v-if="type === 'treatment'"  type="hidden" name="treatment_user_id" :value="treatment_user_id" />
        <input v-if="type === 'package'"    type="hidden" name="prestations"       :value="JSON.stringify(prestations)" />
        <input v-if="type === 'package'"    type="hidden" name="package_user_id"   :value="package_user_id" />

    </div>
</template>

<script>
import moment from 'moment'
import ApiManager from './Calendar/_core/ApiManager'

export default {
    name: 'calenders',

    props: [
        'primaryColor',
        'user',
        'prestation',
        'staff',
        'type',
        'establishments',
        'serie_user_id',
        'treatment_user_id',
        'package_user_id',
        'voucher1', 'voucher2', 'voucher3',
        'sprs',
        'spsp',
    ],

    data() {
        return {
            /* Calendrier */
            currentMonth:    new Date().getMonth() + 1,
            currentYear:     new Date().getFullYear(),
            dayNames:        ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'],
            availableDays:   [],   // ['2026-02-19', ...]
            customers: [],

            /* Sélections */
            selectedDate:    null,  // '2026-02-19'
            currentStaff:    { id: 0, first_name: 'Pas de préférence' },
            currentSlot:     null,  // { time: '09:30', ... }

            /* Données */
            staffList:       [{ id: 0, first_name: 'Pas de préférence' }],
            slots:           [],
            timeSlots: [],

            /* Form / compat ancien composant */
            selectedSlot:    {},
            selectPracti:    null,
            selectSpace:     null,
            allPrestations:  [],
            prestations:     [],
            spaces_list:     null,
            article_quantity: 1,
            nb_user:         1,
            establishment:   {},
            isPrestationAvailable: true,

            /* UI */
            isLoading:       true,
            currentStep:     1,
            isSubmitting: false,
            
        }
    },

    computed: {
        /* Label "Février 2026" */
        monthLabel() {
            const d = new Date(this.currentYear, this.currentMonth - 1, 1)
            return d.toLocaleString('fr-FR', { month: 'long', year: 'numeric' })
        },

        /* Empêche de revenir avant le mois courant */
        isCurrentMonth() {
            const now = new Date()
            return this.currentMonth === now.getMonth() + 1 && this.currentYear === now.getFullYear()
        },

        /* Nb de jours dans le mois */
        daysInMonth() {
            return new Date(this.currentYear, this.currentMonth, 0).getDate()
        },

        /* Offset lundi=0 (lundi premier jour) */
        firstDayOffset() {
            const day = new Date(this.currentYear, this.currentMonth - 1, 1).getDay()
            return day === 0 ? 6 : day - 1
        },

        /* Date formatée pour le résumé */
        summaryDate() {
            if (!this.selectedDate) return ''
            const [y, m, d] = this.selectedDate.split('-').map(Number)
            return new Date(y, m - 1, d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
        },

        /* Créneaux filtrés selon le praticien sélectionné */
        // filteredSlots() {
        //     if (!this.currentStaff || this.currentStaff.id === 0) return this.slots
        //     return this.slots.filter(s => {
        //         if (!s.staff_id) return true
        //         return s.staff_id === this.currentStaff.id
        //     })
        // },
        filteredTimeSlots() {
            if (!this.currentStaff || this.currentStaff.id === 0) {
                return this.timeSlots
            }

            return this.timeSlots.filter(slot => {
                // 🔥 Utiliser practi au lieu de staff_id
                if (!slot.practi || slot.practi === 0) return true
                
                if (Array.isArray(slot.practi)) {
                    // Vérifier si le staff sélectionné est dans la liste
                    return slot.practi.some(p => p && p.id === this.currentStaff.id)
                }
                
                return true
            })
        },

        maxBookableQuantity() {
            return this.maxBookableArticleQuantity * this.participantsPerUnit
        },

        maxBookableArticleQuantity() {
            const spaces = Array.isArray(this.spaces_list) ? this.spaces_list : []
            const maxCapacity = spaces
                .filter(space => space.type === 'public')
                .reduce((max, space) => Math.max(max, parseInt(space.capacity || 1, 10)), 1)
            return Math.max(1, Math.floor(maxCapacity / this.participantsPerUnit))
        },

        participantsPerUnit() {
            const type = parseInt(this.prestation?.type || 1, 10)
            return Math.max(1, type > 0 ? type : 1)
        },

        participantQuantityLabel() {
            if (this.participantsPerUnit <= 1) {
                return this.nb_user
            }

            return `${this.article_quantity} x ${this.participantsPerUnit} = ${this.nb_user}`
        },

        canBookMultiple() {
            return this.type === 'prestation' && this.maxBookableArticleQuantity > 1
        }
    },

    async mounted() {

    
        moment.locale('fr')

    this.establishment = this.establishments
    this.setArticleQuantity(1, false)

    // ✅ CORRECTION : Utiliser prestation directe pour treatment/serie
    if (this.type === 'treatment' || this.type === 'serie') {
        console.log('✅ Mode treatment/serie : utilisation prestation directe')
        
        const rowId = 'direct_' + this.prestation.id  // ← Créer le rowId
        
        this.prestations = [{
            rowId: rowId,  // ← 'direct_2'
            always_first: this.prestation.always_first || false,
            always_last: this.prestation.always_last || false,
            commercial_time: this.prestation.commercial_time || this.prestation.duration,
            name: this.prestation.name,
            id: this.prestation.id,
            item: this.prestation,
            type: 'App\\Models\\Prestation\\Prestation'
        }]
        
        // ✅ FIX : Utiliser rowId au lieu de ID
        this.sprs = [rowId]  // ← ['direct_2']
        
        console.log('✅ Prestations configurées:', this.prestations)
        console.log('✅ sprs configuré:', this.sprs)
        
    } else if (this.type === 'package') {
        console.log('✅ Mode package')
        
        this.prestations = [{
            rowId: 'package_' + this.prestation.id,
            id: this.prestation.id,
            item: this.prestation,
            type: 'App\\Models\\Package',
            name: this.prestation.name,
            commercial_time: this.prestation.commercial_time || this.prestation.duration,
        }]
        
        this.sprs = [this.prestation.id]
        
    } else {
        // Mode panier normal
        console.log('✅ Mode panier : chargement depuis API')
        await this.getCartPrestation()
        const cartQuantity = parseInt(this.prestations?.[0]?.qty || 1, 10)
        this.setArticleQuantity(cartQuantity, false)
    }

    console.log('📊 État final:', {
        prestations: this.prestations,
        sprs: this.sprs,
        nb_user: this.nb_user,
    })

    if (!this.prestations || !this.prestations.length) {
        console.error('❌ ERREUR : Aucune prestation chargée')
        alert('Erreur : Impossible de charger les prestations.')
        return
    }

    await this.getSpacesEsta()
    this.setArticleQuantity(Math.min(this.article_quantity, this.maxBookableArticleQuantity), false)
    await this.loadAvailableDays()
        },

    methods: {

        /* ── Navigation mois ─────────────────────────── */
        async prevMonth() {
            if (this.isCurrentMonth) return
            this.currentMonth--
            if (this.currentMonth < 1) { this.currentMonth = 12; this.currentYear-- }
            this.resetSelections()
            await this.loadAvailableDays()
        },

        async nextMonth() {
            this.currentMonth++
            if (this.currentMonth > 12) { this.currentMonth = 1; this.currentYear++ }
            this.resetSelections()
            await this.loadAvailableDays()
        },

        resetSelections() {
            this.selectedDate = null
            this.currentSlot  = null
            this.slots        = []
            this.staffList    = [{ id: 0, first_name: 'Pas de préférence' }]
            this.currentStaff = { id: 0, first_name: 'Pas de préférence' }
            this.currentStep  = 1
        },

        /* ── Helpers calendrier ──────────────────────── */
        isToday(day) {
            const now = new Date()
            return day === now.getDate()
                && this.currentMonth === now.getMonth() + 1
                && this.currentYear  === now.getFullYear()
        },

        isPast(day) {
            const now  = new Date(); now.setHours(0,0,0,0)
            const cell = new Date(this.currentYear, this.currentMonth - 1, day)
            return cell < now
        },

        hasSlot(day) {
            const key = `${this.currentYear}-${String(this.currentMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`
            return this.availableDays.includes(key)
        },

        isSelected(day) {
            const key = `${this.currentYear}-${String(this.currentMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`
            return this.selectedDate === key
        },

        initials(staff) {
            if (!staff.first_name) return '?'
            return staff.first_name.slice(0, 2).toUpperCase()
        },

        /* ── Sélections ──────────────────────────────── */
        async selectDay(day) {
            if (this.isPast(day)) return
            if (!this.hasSlot(day)) return
            const key = `${this.currentYear}-${String(this.currentMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`
            this.selectedDate = key
            this.currentSlot  = null
            this.currentStep  = 2
            await this.loadSlots()
        },

        selectStaff(staff) {
            this.currentStaff = staff
            this.currentSlot  = null
            this.currentStep  = 2
        },

        selectSlot(slot) {
            this.currentSlot = slot
            this.currentStep = 3
        },

        async setParticipantCount(quantity, reload = true) {
            return this.setArticleQuantity(Math.ceil(parseInt(quantity || 1, 10) / this.participantsPerUnit), reload)
        },

        async setArticleQuantity(quantity, reload = true) {
            const hasSpaces = Array.isArray(this.spaces_list) && this.spaces_list.length > 0
            const maxQuantity = hasSpaces ? this.maxBookableArticleQuantity : Math.max(1, parseInt(quantity || 1, 10))
            const nextQuantity = Math.max(1, Math.min(parseInt(quantity || 1, 10), maxQuantity))
            this.article_quantity = nextQuantity
            this.nb_user = this.article_quantity * this.participantsPerUnit
            this.customers = Array.from({ length: this.nb_user }, (_, index) => {
                if (index === 0) {
                    return { first_name: this.user.first_name, last_name: this.user.last_name }
                }

                return { first_name: '', last_name: '' }
            })

            this.currentSlot = null
            this.timeSlots = []

            if (!reload) return

            await this.loadAvailableDays()
            if (this.selectedDate) {
                await this.loadSlots()
            }
        },

        /* ── API : jours disponibles ─────────────────── */
        async loadAvailableDays() {
    console.log('🔄 loadAvailableDays DÉBUT') // ← AJOUT
    console.log('📦 Données envoyées:', {      // ← AJOUT
        month: this.currentMonth,
        year: this.currentYear,
        prestations: this.prestations,
        sprs: this.sprs,
        establishment: this.establishment,
    })
    
    this.isLoading = true
    
    try {
        const res = await fetch('/api/cart-available-days', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
            },
            body: JSON.stringify({
                prestation_id: this.prestation.id,
                month: this.currentMonth,
                year:  this.currentYear,
                staff_id:           null,
                nbr_user:           this.nb_user,
                establishment:      this.establishment,
                staffs:             this.staff,
                spaces:             this.spaces_list,
                nb_user:            this.nb_user,
                prestations:        this.prestations,
                numberUser:         this.nb_user,
                type:               this.prestation.type,
                id_esta:            this.establishment.id,
                selectedPrestations: this.sprs,
            }),
        })
        
        console.log('📡 Réponse HTTP status:', res.status) // ← AJOUT
        
        this.availableDays = await res.json()
        
        console.log('✅ Jours disponibles reçus:', this.availableDays) // ← AJOUT
        
    } catch (e) {
        console.error('❌ Erreur loadAvailableDays:', e) // ← AJOUT
        this.availableDays = []
    }
    
    this.isLoading = false
    
    console.log('🏁 loadAvailableDays FIN') // ← AJOUT
},

        /* ── API : créneaux + extraction des staffs ──── */
        // async loadSlots() {
        //     this.isLoading = true
        //     this.slots     = []
        //     console.log("selectedPrestations",this.sprs)

        //     try {
        //         const res = await fetch('/api/available-slots', {
        //             method: 'POST',
        //             headers: {
        //                 'Content-Type': 'application/json',
        //                 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
        //             },
        //             body: JSON.stringify({
        //                 date:                this.selectedDate,
        //                 staff_id:            null,
        //                 nbr_user:            this.nb_user,
        //                 establishment:       this.establishment,
        //                 staffs:              this.staff,
        //                 spaces:              this.spaces_list,
        //                 nb_user:             this.nb_user,
        //                 prestations:         this.prestations,
        //                 numberUser:          this.nb_user,
        //                 type:                this.prestation.type,
        //                 id_esta:             this.establishment.id,
        //                 selectedPrestations: this.sprs,
        //             }),
        //         })
        //         const data = await res.json()
        //         this.slots = data || []
        //         this.buildStaffList(data)
        //     } catch (e) {
        //         console.error('loadSlots:', e)
        //         this.slots = []
        //     }
        //     this.isLoading = false
        // },

        // async loadSlots() {
        //     console.log('in loadslots')
        //     this.slots     = []
        //     this.timeSlots = []
        //     this.isLoading = true

        //     try {
        //         if (this.type !== 'package' && this.type !== 'serie' && this.type !== 'treatment') {
        //             console.log('in prestation')

        //             const response = await axios.post(`/reservation/getTimeSlot/2`, {
        //                 establishment:       this.establishment,
        //                 staffs:              this.staff,
        //                 spaces:              this.spaces_list,
        //                 nb_user:             this.nb_user,
        //                 prestations:         this.prestations,
        //                 numberUser:          this.nb_user,
        //                 type:                this.prestation.type,
        //                 id_esta:             this.establishment.id,
        //                 date:                moment(this.selectedDate).format('x'),
        //                 selectedPrestations: this.sprs,
        //             })

        //             const data = response.data || []
        //             console.log('response', data)

        //             this.slots = data

        //             // Extraire les créneaux — data[i][0] contient le créneau principal
        //             this.timeSlots = data.map(slotGroup => {
        //                 const slot = slotGroup[0]
        //                 return {
        //                     time:     slot.date.split(' ')[1].substring(0, 5), // "09:00"
        //                     date:     slot.date,
        //                     staff_id: slot.practi === 0 ? null : (slot.practi[0]?.id ?? null),
        //                     practi:   slot.practi,
        //                 }
        //             })

        //             await this.buildStaffList()

        //         } else {
        //             console.log('in package')

        //             const response = await axios.post(`/forfait/getTimeSlot`, {
        //                 establishment: this.establishment,
        //                 staffs:        this.staff,
        //                 spaces:        this.spaces_list,
        //                 nb_user:       this.nb_user,
        //                 prestations:   [this.prestation],
        //                 numberUser:    this.nb_user,
        //                 type:          this.prestation.type,
        //                 id_esta:       this.establishment.id,
        //                 date:          moment(this.date).format('x'),
        //             })

        //             const data = response.data || []
        //             console.log('response', data)

        //             this.slots = data

        //             this.timeSlots = data.map(slotGroup => {
        //                 const slot = slotGroup[0]
        //                 return {
        //                     time:     slot.date.split(' ')[1].substring(0, 5),
        //                     date:     slot.date,
        //                     staff_id: slot.practi === 0 ? null : (slot.practi[0]?.id ?? null),
        //                     practi:   slot.practi,
        //                 }
        //             })

        //             await this.buildStaffList()
        //         }

        //     } catch (e) {
        //         console.error('loadSlots error:', e)
        //     } finally {
        //         this.isLoading = false
        //     }
        // },
        async loadSlots() {
    console.log('🔄 loadSlots appelée')
    console.log('   selectedDate:', this.selectedDate)
    console.log('   type:', this.type)
    
    this.slots     = []
    this.timeSlots = []
    this.isLoading = true

    try {
        // ✅ CORRECTION : treatment et serie utilisent aussi /reservation/getTimeSlot/2
        if (this.type === 'package') {
            console.log('   → Mode PACKAGE')

            const response = await axios.post(`/forfait/getTimeSlot`, {
                establishment: this.establishment,
                staffs:        this.staff,
                spaces:        this.spaces_list,
                nb_user:       this.nb_user,
                prestations:   [this.prestation],
                numberUser:    this.nb_user,
                type:          this.prestation.type,
                id_esta:       this.establishment.id,
                date:          moment(this.selectedDate).format('x'),
            })

            const data = response.data || []
            console.log('   ✅ Response package:', data)

            this.slots = data

            this.timeSlots = data.map(slotGroup => {
                const slot = slotGroup[0]
                return {
                    time:     slot.date.split(' ')[1].substring(0, 5),
                    date:     slot.date,
                    staff_id: slot.practi === 0 ? null : (slot.practi[0]?.id ?? null),
                    practi:   slot.practi,
                }
            })

            await this.buildStaffList()

        } else {
            // ✅ Mode prestation normale, treatment, serie
            console.log('   → Mode PRESTATION/TREATMENT/SERIE')

            const response = await axios.post(`/reservation/getTimeSlot/2`, {
                establishment:       this.establishment,
                staffs:              this.staff,
                spaces:              this.spaces_list,
                nb_user:             this.nb_user,
                prestations:         this.prestations,
                numberUser:          this.nb_user,
                type:                this.prestation.type,
                id_esta:             this.establishment.id,
                date:                moment(this.selectedDate).format('x'),
                selectedPrestations: this.sprs,  // ✅ Envoyer sprs
            })

            const data = response.data || []
            console.log('   ✅ Response prestation:', data)

            this.slots = data

            // Extraire les créneaux
            this.timeSlots = data.map(slotGroup => {
                const slot = slotGroup[0]
                return {
                    time:     slot.date.split(' ')[1].substring(0, 5), // "09:00"
                    date:     slot.date,
                    staff_id: slot.practi === 0 ? null : (Array.isArray(slot.practi) ? slot.practi[0]?.id : null),
                    practi:   slot.practi,
                    space:    slot.space,
                    rowId:    slot.rowId,
                }
            })

            await this.buildStaffList()
        }

    } catch (e) {
        console.error('❌ loadSlots error:', e.response?.data || e.message)
        this.slots = []
        this.timeSlots = []
    } finally {
        this.isLoading = false
    }
    
    console.log('🏁 loadSlots terminée')
    console.log('   timeSlots count:', this.timeSlots.length)
},
        /* Construit la liste des praticiens depuis les créneaux.
           Si l'API retourne { time, staff_id, staff_name } on s'en sert,
           sinon on repart sur la prop `staff` */
        async buildStaffList() {
            const list = [{ id: 0, first_name: 'Pas de préférence' }]
            console.log('this.prestation.id',this.prestation.id)
            try {
                const res = await axios.get(`api/prestation/staff/${this.prestation.id}`)
                const staffs = res.data || []

                staffs.forEach(s => {
                    if (!list.find(l => l.id === s.id)) {
                        list.push({
                            id:         s.id,
                            first_name: s.first_name,
                            image:      s.image || null,
                        })
                    }
                })
            } catch (e) {
                console.error('buildStaffList:', e)
            }

            this.staffList    = list.sort((a, b) => a.first_name.localeCompare(b.first_name))
            this.currentStaff = list[0]
        },
        
        async buildStaffList() {
    const list = [{ id: 0, first_name: 'Pas de préférence' }]
    const forceNoPreference = this.slots?.some(slotGroup =>
        slotGroup?.some(slot => slot?.force_no_preference === true)
    )

    if (forceNoPreference) {
        this.staffList = list
        this.currentStaff = list[0]
        return
    }

    // Extraire les praticiens depuis les slots déjà chargés
    if (this.slots && this.slots.length) {
        this.slots.forEach(slotGroup => {
            const practi = slotGroup[0]?.practi
            if (Array.isArray(practi)) {
                practi.forEach(s => {
                    if (s && !list.find(l => l.id === s.id)) {
                        list.push({
                            id:         s.id,
                            first_name: s.first_name,
                            last_name:  s.last_name,
                            image:      s.image || null,
                        })
                    }
                })
            }
        })
    }

    // Fallback API si aucun praticien trouvé dans les slots
    if (list.length === 1) {
        try {
            const res = await axios.get(`/soin/getStaffsByPresta/${this.prestation.id}`)
            ;(res.data || []).forEach(s => {
                if (!list.find(l => l.id === s.id)) {
                    list.push({ id: s.id, first_name: s.first_name, image: s.image || null })
                }
            })
        } catch (e) {
            console.error('buildStaffList fallback:', e)
        }
    }

    // 🔥 TRIER EN GARDANT "Pas de préférence" EN PREMIER
    const pasDePreference = list[0]  // Sauvegarder "Pas de préférence"
    const otherStaffs = list.slice(1).sort((a, b) => 
        a.first_name.localeCompare(b.first_name, 'fr')
    )
    
    this.staffList = [pasDePreference, ...otherStaffs]
    this.currentStaff = pasDePreference
},

        /* ── Confirmation ────────────────────────────── */
        // async confirm() {
        //     if (!this.currentSlot) return

        //     // Reconstituer la structure attendue par le backend
        //     const slotData = this.currentSlot  // contient date, practi, space, rowId, prestation...

        //     this.selectedSlot = {
        //         prestation: this.slots.map(slotGroup => slotGroup[0]),  // tableau comme l'ancien
        //         start_first_time: parseInt(moment(slotData.date).format('X')),
        //         end_first_time:   parseInt(moment(slotData.date).format('X')) + parseInt(this.prestation.duration * 60),
        //         day:              this.selectedDate,
        //         pref_prati:       this.currentStaff ? this.currentStaff.id : 0,
        //     }

        //     this.selectPracti = this.currentStaff
        //     this.allPrestations.push(this.selectedSlot)

        //     try {
        //         if (['treatment', 'serie'].includes(this.type)) {
        //             setTimeout(() => { document.querySelector('form').submit() }, 300)
        //         } else {
        //             const response = await axios.post('/soin/reservation/confirmation', {
        //                 all:               JSON.stringify(this.selectedSlot),
        //                 nbUser:            this.nb_user,
        //                 preference_practi: this.currentStaff ? this.currentStaff.id : 0,
        //             })
        //             if (response?.status === 200 && response?.data?.redirect) {
        //                 window.location.href = response.data.redirect
        //             } else {
        //                 document.querySelector('form').submit()
        //             }
        //         }
        //     } catch {
        //         document.querySelector('form').submit()
        //     }
        // },
        // ✅ APRÈS — envoie uniquement le groupe du créneau sélectionné
        async confirm() {
            if (!this.currentSlot || this.isSubmitting) return
            this.isSubmitting = true
            const slotData = this.currentSlot

            // Trouver le groupe correspondant au créneau cliqué (comme V1 : ts passé à selectMultipleSlot)
            const selectedSlotGroup = this.slots.find(slotGroup =>
                slotGroup[0].date === slotData.date
            )

            if (!selectedSlotGroup) return

            this.selectedSlot = {
                // start: parseInt(moment(slotData.date).format('X')),
                prestation: selectedSlotGroup,  // ← UN seul groupe avec toutes les prestations chaînées
                start_first_time: parseInt(moment(slotData.date).format('X')),
                end_first_time:   parseInt(moment(slotData.date).format('X')) + parseInt(this.prestation.duration * 60),
                day:              this.selectedDate,
                pref_prati:       this.currentStaff ? this.currentStaff.id : 0,
            }

            this.selectPracti = this.currentStaff
            this.allPrestations.push(this.selectedSlot)

            try {
                if (['treatment', 'serie'].includes(this.type)) {
                    setTimeout(() => { document.querySelector('form').submit() }, 300)
                } else {
                    const response = await axios.post('/soin/reservation/confirmation', {
                        all:               JSON.stringify(this.selectedSlot),
                        nbUser:            this.nb_user,
                        customers:         this.customers,  // ← manquait en V2
                        preference_practi: this.currentStaff ? this.currentStaff.id : 0,
                    })
                    if (response?.status === 200 && response?.data?.redirect) {
                        window.location.href = response.data.redirect
                    } else {
                        document.querySelector('form').submit()
                    }
                }
            } catch 
            // {
            //     // this.allPrestations.push(this.selectedSlot)
            //     // document.querySelector('form').submit()

            // }
            (error) {
                console.error('confirm() - erreur axios', {
                    message: error?.message,
                    status:  error?.response?.status,
                    data:    error?.response?.data,
                    url:     '/soin/reservation/confirmation',
                    payload: {
                        all:               JSON.stringify(this.selectedSlot),
                        nbUser:            this.nb_user,
                        preference_practi: this.currentStaff ? this.currentStaff.id : 0,
                    }
                })

                // Optionnel : afficher l'erreur à l'utilisateur
                // alert('Une erreur est survenue lors de la confirmation. Voir la console pour les détails.')

                // Fallback : soumission du formulaire
                // document.querySelector('form').submit()
            } finally {
                this.isSubmitting = false
            }
        },

        /* ── Compat helpers (ancien composant) ───────── */
        async getCartPrestation() {
            const res = await axios.get('/getCartPrestations')
            this.prestations = res.data || []
        },

        async getSpacesEsta() {
            const res = await axios.post(`/soin/getSpaces/${this.establishment.id}`, { prestation_id: this.prestation.id })
            this.spaces_list = res.data
        },
    },
}
</script>

<style scoped>
/* ════════════════════════════════════════════════
   Variables
════════════════════════════════════════════════ */
.picker {
    --navy:       #0a1a2f;
    --navy-light: rgba(10,26,47,0.07);
    --navy-mid:   rgba(10,26,47,0.45);
    --font:       'DM Sans', sans-serif;

    font-family:    var(--font);
    background:     white;
    border-radius:  24px;
    width:          100%;
    max-width:      680px;
    margin:         0 auto;
    box-shadow:     0 4px 40px rgba(0,0,0,0.08);
    position:       relative;
    overflow:       hidden;
}

/* ── Header ────────────────────────────────────── */
.picker__header {
    padding:       24px 32px 20px;
    border-bottom: 1px solid #eee;
    display:       flex;
    align-items:   center;
    justify-content: space-between;
}

.picker__title {
    font-size:   1.1rem;
    font-weight: 600;
    color:       var(--navy);
    letter-spacing: -0.01em;
}

/* ── Body ──────────────────────────────────────── */
.picker__body {
    padding:  28px 32px;
    position: relative;
    min-height: 120px;
}

/* ── Loader ────────────────────────────────────── */
.picker__loader {
    position:        absolute;
    inset:           0;
    background:      rgba(255,255,255,.85);
    display:         flex;
    align-items:     center;
    justify-content: center;
    z-index:         10;
    border-radius:   24px;
}

.spinner {
    width:       38px;
    height:      38px;
    border:      3px solid #eee;
    border-top:  3px solid var(--navy);
    border-radius: 50%;
    animation:   spin .8s linear infinite;
}
@keyframes spin { 100% { transform: rotate(360deg); } }

/* ── Calendrier ────────────────────────────────── */
.cal-nav {
    display:         flex;
    align-items:     center;
    justify-content: space-between;
    margin-bottom:   20px;
}

.cal-nav__month {
    font-size:      1rem;
    font-weight:    600;
    color:          var(--navy);
    text-transform: capitalize;
    letter-spacing: -.01em;
}

.cal-nav__btn {
    background:    none;
    border:        1.5px solid #ddd;
    border-radius: 50%;
    width:         34px;
    height:        34px;
    cursor:        pointer;
    display:       flex;
    align-items:   center;
    justify-content: center;
    color:         var(--navy);
    font-size:     1.1rem;
    transition:    border-color .2s, background .2s;
}
.cal-nav__btn:hover:not(:disabled) { border-color: var(--navy); background: var(--navy-light); }
.cal-nav__btn:disabled             { opacity: .3; cursor: not-allowed; }

.cal-grid {
    display:               grid;
    grid-template-columns: repeat(7, 1fr);
    gap:                   4px;
}

.cal-day-name {
    text-align:     center;
    font-size:      .72rem;
    font-weight:    500;
    color:          #aaa;
    letter-spacing: .06em;
    text-transform: uppercase;
    padding:        8px 0;
}

.cal-day {
    aspect-ratio:    1;
    display:         flex;
    align-items:     center;
    justify-content: center;
    border-radius:   10px;
    font-size:       .88rem;
    color:           var(--navy);
    cursor:          pointer;
    transition:      background .18s, color .18s;
    position:        relative;
}

.cal-day:hover:not(.cal-day--disabled):not(.cal-day--empty) {
    background: var(--navy-light);
}

.cal-day--empty    { cursor: default; }
.cal-day--disabled { color: #ccc; cursor: not-allowed; }
.cal-day--today    { font-weight: 700; border: 1.5px solid var(--navy); }

.cal-day--has-slot::after {
    content:    '';
    position:   absolute;
    bottom:     5px;
    left:       50%;
    transform:  translateX(-50%);
    width:      4px;
    height:     4px;
    border-radius: 50%;
    background: #3ecfb2;
}

.cal-day--selected {
    background:  var(--navy) !important;
    color:       white !important;
    font-weight: 600;
}

/* ── Légende ───────────────────────────────────── */
.legend {
    display:     flex;
    gap:         16px;
    margin-top:  14px;
    font-size:   .75rem;
    color:       #aaa;
    align-items: center;
}
.legend-dot {
    display:       inline-block;
    width:         6px; height: 6px;
    border-radius: 50%;
    background:    #3ecfb2;
    margin-right:  4px;
}

/* ── Séparateur ────────────────────────────────── */
.divider {
    height:     1px;
    background: #eee;
    margin:     24px 0;
}

/* ── Step title ────────────────────────────────── */
.step-title {
    font-size:      .72rem;
    font-weight:    500;
    letter-spacing: .12em;
    text-transform: uppercase;
    color:          #aaa;
    margin-bottom:  14px;
}

/* ── Staff pills ───────────────────────────────── */
.staff-grid {
    display:       flex;
    flex-wrap:     wrap;
    gap:           10px;
    margin-bottom: 4px;
}

.staff-card {
    display:       flex;
    align-items:   center;
    gap:           10px;
    padding:       9px 16px;
    border-radius: 50px;
    border:        1.5px solid #e0e0e0;
    cursor:        pointer;
    font-size:     .88rem;
    font-weight:   500;
    color:         var(--navy);
    transition:    border-color .2s, background .2s, color .2s;
    user-select:   none;
}
.staff-card:hover  { border-color: var(--navy); background: var(--navy-light); }
.staff-card.active { background: var(--navy); color: white; border-color: var(--navy); }

.staff-avatar {
    width:         30px; height: 30px;
    border-radius: 50%;
    background:    #e8e8e8;
    display:       flex;
    align-items:   center;
    justify-content: center;
    font-size:     .72rem;
    font-weight:   700;
    overflow:      hidden;
    flex-shrink:   0;
}
.staff-avatar img        { width: 100%; height: 100%; object-fit: cover; }
.staff-card.active .staff-avatar { background: rgba(255,255,255,.18); }

.quantity-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.qty-btn {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 1px solid var(--navy-mid);
    background: white;
    color: var(--navy);
    font-size: 20px;
    line-height: 1;
}

.qty-btn:disabled {
    opacity: .35;
}

.qty-value {
    min-width: 28px;
    text-align: center;
    font-weight: 700;
}

/* ── Créneaux ──────────────────────────────────── */
.slots-row {
    display:   flex;
    flex-wrap: wrap;
    gap:       8px;
    justify-content: center; 
}

.slot {
    padding:       9px 18px;
    border-radius: 50px;
    border:        1.5px solid #e0e0e0;
    font-size:     .88rem;
    font-weight:   500;
    color:         var(--navy);
    cursor:        pointer;
    transition:    border-color .2s, background .2s, color .2s, transform .15s;
    user-select:   none;
}
.slot:hover  { border-color: var(--navy); background: var(--navy-light); transform: translateY(-1px); }
.slot.active { background: var(--navy); color: white; border-color: var(--navy); }

/* ── Empty state ───────────────────────────────── */
.empty {
    text-align: center;
    padding:    28px 0;
    color:      #bbb;
    font-size:  .9rem;
}
.empty svg      { display: block; margin: 0 auto 12px; opacity: .25; }
.empty-text     { font-size: .88rem; color: #bbb; font-style: italic; }

/* ── Footer ────────────────────────────────────── */
.picker__footer {
    padding:         20px 32px 28px;
    display:         flex;
    align-items:     center;
    justify-content: space-between;
    border-top:      1px solid #eee;
    gap:             16px;
}

.summary            { font-size: .88rem; color: var(--navy-mid); line-height: 1.55; }
.summary strong     { color: var(--navy); font-weight: 600; }
.summary--empty     { font-style: italic; color: #bbb; }

.btn-confirm {
    font-family:   var(--font);
    font-size:     .88rem;
    font-weight:   500;
    color:         white;
    background:    var(--navy);
    border:        none;
    border-radius: 50px;
    padding:       12px 24px;
    cursor:        pointer;
    display:       inline-flex;
    align-items:   center;
    gap:           8px;
    white-space:   nowrap;
    transition:    opacity .2s, transform .15s;
    flex-shrink:   0;
}
.btn-confirm:hover:not(:disabled)    { opacity: .85; transform: translateY(-1px); }
.btn-confirm:active                  { transform: scale(.97); }
.btn-confirm--disabled               { opacity: .35 !important; cursor: not-allowed !important; transform: none !important; }

/* ── Responsive ────────────────────────────────── */
@media (max-width: 600px) {
    .picker__header,
    .picker__body,
    .picker__footer { padding-left: 18px; padding-right: 18px; }

    .picker__footer { flex-direction: column; align-items: flex-start; gap: 14px; }
    .btn-confirm    { width: 100%; justify-content: center; }
}

@media (max-width: 500px) {
    .sizer {
        padding: 0px !important;
    }
    
    .picker__header,
    .picker__body,
    .picker__footer { 
        padding-left: 12px; 
        padding-right: 12px; 
    }
    
    .cal-grid {
        gap: 2px;
    }
    
    .cal-day {
        font-size: .78rem;
        border-radius: 7px;
    }
    
    .cal-day-name {
        font-size: .65rem;
        padding: 6px 0;
    }
}

@media (max-width: 450px) {

    /* ─────────────────────────────
       Structure générale
    ───────────────────────────── */
    .picker__header,
    .picker__body,
    .picker__footer {
        padding-left: 10px !important;
        padding-right: 10px !important;
    }

    .picker__body {
        padding-top: 22px;
        padding-bottom: 22px;
    }

    /* ─────────────────────────────
       Grille calendrier
    ───────────────────────────── */
    .cal-grid {
        width: 100%;
        grid-template-columns: repeat(7, minmax(0, 1fr));
        gap: 3px;
    }

    /* ─────────────────────────────
       Jours (équilibre largeur / hauteur)
    ───────────────────────────── */
    .cal-day {
        aspect-ratio: 1 / 1;
        font-size: 0.72rem;
        border-radius: 6px;
        padding: 0;
        transition: background .18s ease, color .18s ease, transform .08s ease;
    }

    /* Hover tactile */
    .cal-day:active:not(.cal-day--disabled) {
        transform: scale(0.94);
    }

    /* Aujourd’hui */
    .cal-day--today {
        border: 1px solid var(--navy);
    }

    /* Sélection */
    .cal-day--selected {
        background: var(--navy) !important;
        color: white !important;
        font-weight: 600;
        border: none;
    }

    /* Past / disabled */
    .cal-day--disabled {
        color: #d4d4d4;
    }

    /* Dot disponibilité */
    .cal-day--has-slot::after {
        width: 3px;
        height: 3px;
        bottom: 4px;
    }

    /* ─────────────────────────────
       Noms des jours
    ───────────────────────────── */
    .cal-day-name {
        font-size: 0.6rem;
        padding: 6px 0;
        letter-spacing: .05em;
    }

    /* ─────────────────────────────
       Navigation mois
    ───────────────────────────── */
    .cal-nav {
        margin-bottom: 16px;
    }

    .cal-nav__btn {
        width: 30px;
        height: 30px;
        font-size: 0.95rem;
        border-width: 1px;
    }

    .cal-nav__month {
        font-size: 0.9rem;
    }

    /* ─────────────────────────────
       Staff pills proportionnelles
    ───────────────────────────── */
    .staff-card {
        padding: 6px 12px;
        font-size: 0.78rem;
        border-width: 1px;
        border-radius: 40px;
    }

    .staff-avatar {
        width: 22px;
        height: 22px;
        font-size: 0.6rem;
    }

    /* ─────────────────────────────
       Slots
    ───────────────────────────── */
    .slot {
        padding: 6px 14px;
        font-size: 0.78rem;
        border-width: 1px;
        border-radius: 40px;
    }

    /* ─────────────────────────────
       Footer
    ───────────────────────────── */
    .summary {
        font-size: 0.82rem;
    }

    .btn-confirm {
        font-size: 0.82rem;
        padding: 10px 20px;
        border-radius: 40px;
    }

    .btn-confirm svg {
        width: 13px;
        height: 13px;
    }
}
@media (max-width: 450px) {

    .cal-day {
        width: 100%;              /* réduit d'environ 1/3 */
        height: 36px;            /* hauteur cohérente */
        margin: 0 auto;          /* centre horizontalement */
        aspect-ratio: auto;      /* on retire le carré strict */
        border-radius: 4px;
        font-size: 0.72rem;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: none;
        transition: background .15s ease, transform .08s ease;
    }

    /* Aujourd'hui sans border */
    .cal-day--today {
        box-shadow: inset 0 0 0 1px var(--navy);
        border: 0.5px solid var(--navy);
    }

    /* Sélection */
    .cal-day--selected {
        background: var(--navy);
        color: white !important;
        box-shadow: none;
    }

    /* Dot dispo repositionné */
    .cal-day--has-slot::after {
        bottom: 3px;
    }
}
</style>
